import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Turnstile from "@/components/Turnstile";
import { useAuth } from "@/hooks/useAuth";
import { verifyTurnstile } from "@/lib/verifyTurnstile";
import { Mail, Lock, ArrowRight } from "lucide-react";

const SignUpPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const { signUp } = useAuth();

  const resetCaptcha = () => {
    setCaptchaToken(null);
    setCaptchaKey((k) => k + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const ok = await verifyTurnstile(captchaToken, "signup");
    if (!ok) {
      setLoading(false);
      resetCaptcha();
      toast({
        title: "Verification failed",
        description: "Please complete the security check and try again.",
        variant: "destructive",
      });
      return;
    }
    const { data, error } = await signUp(email, password);
    setLoading(false);
    if (error) {
      resetCaptcha();
      const msg = error.message?.toLowerCase() ?? "";
      if (msg.includes("already") || msg.includes("registered") || msg.includes("exists")) {
        toast({
          title: "Email already registered",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Sign up failed", description: error.message, variant: "destructive" });
      }
      return;
    }
    // Supabase returns a user with empty identities array when the email is already registered
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      resetCaptcha();
      toast({
        title: "Email already registered",
        description: "An account with this email already exists. Please sign in instead.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Account created!", description: "Check your email for a verification link, then sign in." });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container flex items-center justify-center py-16 md:py-24">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8 shadow-[var(--card-shadow)]">
            <div className="text-center mb-8">
              <img src="/logo.png" alt="Vecurate" className="w-20 h-20 object-contain mx-auto mb-4" />
              <h1 className="font-serif text-2xl text-foreground">Create Account</h1>
              <p className="text-sm text-muted-foreground mt-1">Join to share your experiences</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10" placeholder="you@example.com" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10" placeholder="••••••••" />
                </div>
              </div>
              <div className="flex justify-center">
                <Turnstile key={captchaKey} action="signup" onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} onError={() => setCaptchaToken(null)} />
              </div>
              <Button type="submit" className="w-full gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading || !captchaToken}>
                {loading ? "Verifying..." : "Sign Up"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignUpPage;
